import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Progress from "@/components/Progress";
import SegmentedButton from "@/components/SegmentedButton";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams } from "@/hooks/useSearchParams";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import TabLayout from "../_TabLayout";
import { useInteractionTopicsAccess } from "../access";
import { shouldSwitchConfigurationToLocalBackend } from "../localBackend";
import ConfigurationLoadError from "../_components/ConfigurationLoadError";
import TopicsEditor, { topicFocusKey } from "./_components/TopicsEditor";
import {
  INTERACTION_TOPIC_CONFIGS_QUERY,
  INTERACTION_TOPIC_DEPARTMENT_PARAM,
  INTERACTION_TOPIC_DEPARTMENTS,
  OTHER_TOPIC,
  constrainInteractionTopicDepartment,
  type InteractionTopicConfig,
  type InteractionTopicInput
} from "./queries";
import { UPDATE_INTERACTION_TOPIC_CONFIG_MUTATION, type UpdateInteractionTopicConfigVariables } from "./mutations";
import {
  diffSavedTopics,
  hasValidationErrors,
  isTopicLocked,
  mergeEchoTopics,
  topicsEqual,
  validateInteractionTopics,
  type TopicFieldChanges
} from "./_utils/topicsValidation";

export const AUTOSAVE_DEBOUNCE_MS = 700;
const SAVE_FLASH_MS = 1800;

function insertBeforeOther<T>(items: T[], item: T, isOther: (value: T) => boolean): T[] {
  const otherIndex = items.findIndex(isOther);
  if (otherIndex === -1) return [...items, item];
  return [...items.slice(0, otherIndex), item, ...items.slice(otherIndex)];
}

function insertionIndexBeforeOther<T>(items: T[], isOther: (value: T) => boolean): number {
  const otherIndex = items.findIndex(isOther);
  return otherIndex === -1 ? items.length : otherIndex;
}

function isOtherName(name: string): boolean {
  return name.trim() === OTHER_TOPIC;
}

// Server lists carry the canonical trailing Other; editing works on the raw
// lines without it (the backend re-appends it on save).
function stripCanonicalOther(subtopics: string[]): string[] {
  const lines = [...subtopics];
  if (lines.length > 0 && isOtherName(lines[lines.length - 1])) lines.pop();
  return lines;
}

function toEditableTopic(topic: InteractionTopicConfig["topics"][number]): InteractionTopicInput {
  return {
    name: topic.name,
    subtopics: stripCanonicalOther(topic.subtopics),
    code: topic.code ?? null,
    defaultPrivate: topic.defaultPrivate ?? null
  };
}

function toSavePayload(topic: InteractionTopicInput): InteractionTopicInput {
  return {
    name: topic.name,
    subtopics: topic.subtopics.map((line) => line.trim()).filter((line) => line.length > 0),
    code: topic.code ?? null,
    defaultPrivate: topic.defaultPrivate ?? null
  };
}

export default function InteractionTopicsTabPage() {
  usePageTitle("/configuration/interaction-topics", "Member 360 Configuration | Interaction Topics");
  const { loaded, canView, departments } = useInteractionTopicsAccess();
  const { params, addSearchParam } = useParams();
  const { setError, clearError } = useErrorBanner();
  const visibleDepartments = useMemo(
    () => INTERACTION_TOPIC_DEPARTMENTS.filter((dept) => departments.includes(dept.value)),
    [departments]
  );
  const department = useMemo(
    () => constrainInteractionTopicDepartment(params[INTERACTION_TOPIC_DEPARTMENT_PARAM], departments),
    [params, departments]
  );

  const [topicsByDept, setTopicsByDept] = useState<Record<string, InteractionTopicInput[]> | null>(null);
  const [lockVersions, setLockVersions] = useState<Record<string, number>>({});
  const [fieldFlash, setFieldFlash] = useState<{
    key: number;
    dept: string;
    changes: TopicFieldChanges;
    deleted: boolean;
  } | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const savedSnapshot = useRef<Record<string, InteractionTopicInput[]>>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveEpoch = useRef(0);
  // Serializes saves so a follow-up save always carries the lock version from
  // the previous save's echo instead of racing it into a false conflict.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const latestTopics = useRef<Record<string, InteractionTopicInput[]> | null>(null);
  const latestDepartment = useRef(department);
  const latestLockVersions = useRef<Record<string, number>>({});
  const conflictedDept = useRef<string | null>(null);

  const skip = !loaded || !canView || shouldSwitchConfigurationToLocalBackend();
  const { data, loading, error, refetch } = useQuery(INTERACTION_TOPIC_CONFIGS_QUERY, {
    skip,
    fetchPolicy: "network-only"
  });

  const [updateTopics] = useMutation(UPDATE_INTERACTION_TOPIC_CONFIG_MUTATION, {
    refetchQueries: [{ query: INTERACTION_TOPIC_CONFIGS_QUERY }]
  });
  const saveTopicsRef = useRef<(dept: string, topics: InteractionTopicInput[]) => Promise<unknown>>(() =>
    Promise.resolve()
  );
  latestTopics.current = topicsByDept;
  latestDepartment.current = department;
  latestLockVersions.current = lockVersions;

  useEffect(() => {
    if (data && !topicsByDept) {
      const initial: Record<string, InteractionTopicInput[]> = {};
      const versions: Record<string, number> = {};
      for (const config of data.interactionTopicConfigs) {
        initial[config.department] = config.topics.map(toEditableTopic);
        versions[config.department] = config.lockVersion ?? 0;
      }
      savedSnapshot.current = initial;
      setTopicsByDept(initial);
      setLockVersions(versions);
    }
  }, [data, topicsByDept]);

  // After a version conflict, the refetch lands here: replace the conflicted
  // department's local state with the server list (the conflicting local edits
  // are discarded — the banner says so).
  useEffect(() => {
    const dept = conflictedDept.current;
    if (!dept || !data || !topicsByDept) return;
    const config = data.interactionTopicConfigs.find((candidate) => candidate.department === dept);
    if (!config) return;
    conflictedDept.current = null;
    const serverTopics = config.topics.map(toEditableTopic);
    savedSnapshot.current[dept] = serverTopics;
    setTopicsByDept((prev) => (prev ? { ...prev, [dept]: serverTopics } : prev));
    setLockVersions((prev) => ({ ...prev, [dept]: config.lockVersion ?? 0 }));
  }, [data, topicsByDept]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      // Flush a pending valid save on unmount so navigating away mid-debounce
      // does not drop edits. Goes through the chained save path (waiting for
      // any in-flight echo's lock version); post-unmount setState is a no-op.
      const dept = latestDepartment.current;
      const pending = latestTopics.current?.[dept];
      const saved = savedSnapshot.current[dept];
      if (
        pending &&
        saved &&
        !topicsEqual(pending, saved) &&
        !hasValidationErrors(validateInteractionTopics(pending))
      ) {
        void saveTopicsRef.current(dept, pending);
      }
    };
  }, []);

  const currentTopics = useMemo(() => topicsByDept?.[department] ?? [], [topicsByDept, department]);
  const errors = useMemo(() => validateInteractionTopics(currentTopics), [currentTopics]);
  const isDirty = useMemo(() => {
    const saved = savedSnapshot.current[department];
    if (!topicsByDept || !saved) return false;
    return !topicsEqual(currentTopics, saved);
  }, [topicsByDept, currentTopics, department]);

  const saveTopicsInner = useCallback(
    async (dept: string, topics: InteractionTopicInput[]) => {
      const epochAtSend = saveEpoch.current;
      clearError();
      try {
        const variables: UpdateInteractionTopicConfigVariables = {
          input: {
            department: dept,
            topics: topics.map(toSavePayload),
            lockVersion: latestLockVersions.current[dept] ?? 0
          }
        };
        const result = await updateTopics({ variables });
        const payload = result.data?.updateInteractionTopicConfig;
        if (payload?.success && payload.config) {
          const prevSnapshot = savedSnapshot.current[dept] ?? [];
          const savedTopics = payload.config.topics.map(toEditableTopic);
          savedSnapshot.current[dept] = savedTopics;
          // Sync the ref immediately: a chained follow-up save reads it in the
          // same tick, before React re-renders with the new state.
          const nextVersion = payload.config?.lockVersion ?? 0;
          latestLockVersions.current = { ...latestLockVersions.current, [dept]: nextVersion };
          setLockVersions((prev) => ({ ...prev, [dept]: nextVersion }));
          // Only sync the server list into edit state when nothing changed
          // mid-flight; otherwise keep the newer local edits (still dirty, so
          // the autosave effect will send them next). The merge keeps local
          // whitespace (in-progress blank lines) wherever content matches.
          // The flash fires only on a fully synced save — never while a
          // follow-up save is still due — and lands on exactly the fields
          // this save persisted.
          if (saveEpoch.current === epochAtSend) {
            setTopicsByDept((prev) =>
              prev ? { ...prev, [dept]: mergeEchoTopics(prev[dept] ?? [], savedTopics) } : prev
            );
            const changes = diffSavedTopics(prevSnapshot, variables.input.topics);
            // A delete-only save has no remaining field to badge, so it gets
            // the page-level toast instead.
            const deleted = variables.input.topics.length < prevSnapshot.length;
            if (Object.keys(changes).length > 0 || deleted) {
              setFieldFlash((prev) => ({ key: (prev?.key ?? 0) + 1, dept, changes, deleted }));
              if (flashTimer.current) clearTimeout(flashTimer.current);
              flashTimer.current = setTimeout(() => setFieldFlash(null), SAVE_FLASH_MS);
            }
          }
        } else if (payload && !payload.success && /another admin saved/i.test(payload.message ?? "")) {
          // Version conflict: another admin saved this department first.
          // Reload the server list; the conflicting local edits are discarded
          // (the banner says so) so the next save carries a fresh version.
          conflictedDept.current = dept;
          setFieldFlash(null);
          const deptLabel = INTERACTION_TOPIC_DEPARTMENTS.find((candidate) => candidate.value === dept)?.label ?? dept;
          setError(
            `${payload.message ?? "Couldn't save interaction topics."} Loading the latest ${deptLabel} topics — conflicting edits were discarded.`
          );
          void refetch();
        } else {
          setError(payload?.message || "Couldn't save interaction topics.");
        }
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Couldn't save interaction topics.");
      }
    },
    [updateTopics, setError, clearError, refetch]
  );

  // Queues saves behind each other so a follow-up save waits for the previous
  // echo (and its lock version) instead of racing it into a false conflict.
  // A rejection never breaks the chain for later saves.
  const saveTopics = useCallback(
    (dept: string, topics: InteractionTopicInput[]) => {
      const run = saveChainRef.current.then(() => saveTopicsInner(dept, topics));
      saveChainRef.current = run.catch(() => {});
      return run;
    },
    [saveTopicsInner]
  );
  saveTopicsRef.current = saveTopics;

  useEffect(() => {
    if (!topicsByDept || !savedSnapshot.current[department]) return;
    if (!isDirty) {
      return;
    }
    if (hasValidationErrors(errors)) {
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void saveTopics(department, currentTopics);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [topicsByDept, department, currentTopics, isDirty, errors, saveTopics]);

  const handleDeptChange = useCallback(
    (value: string | null) => {
      if (!value || value === department) return;
      const current = topicsByDept?.[department];
      const saved = savedSnapshot.current[department];
      if (
        current &&
        saved &&
        !topicsEqual(current, saved) &&
        !hasValidationErrors(validateInteractionTopics(current))
      ) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        void saveTopics(department, current);
      }
      addSearchParam(INTERACTION_TOPIC_DEPARTMENT_PARAM, value);
    },
    [department, topicsByDept, saveTopics, addSearchParam]
  );

  const updateDeptTopics = useCallback(
    (dept: string, updater: (topics: InteractionTopicInput[]) => InteractionTopicInput[]) => {
      saveEpoch.current += 1;
      setTopicsByDept((prev) => {
        if (!prev) return prev;
        return { ...prev, [dept]: updater(prev[dept] ?? []) };
      });
    },
    []
  );

  const handleChangeTopicName = useCallback(
    (topicIndex: number, name: string) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.map((topic, index) => (index === topicIndex ? { ...topic, name } : topic));
      });
    },
    [department, updateDeptTopics]
  );

  const handleChangeSubtopics = useCallback(
    (topicIndex: number, text: string) => {
      // Store the raw textarea lines untouched — trimming and the canonical
      // Other happen at save serialization, never while typing.
      const lines = text.split("\n");
      updateDeptTopics(department, (topics) =>
        topics.map((topic, index) => (index === topicIndex ? { ...topic, subtopics: lines } : topic))
      );
    },
    [department, updateDeptTopics]
  );

  const handleAddTopic = useCallback(() => {
    const topics = topicsByDept?.[department] ?? [];
    const insertAt = insertionIndexBeforeOther(topics, (topic) => isOtherName(topic.name));
    updateDeptTopics(department, (current) =>
      insertBeforeOther(current, { name: "", subtopics: [], code: null, defaultPrivate: null }, (topic) =>
        isOtherName(topic.name)
      )
    );
    setFocusKey(topicFocusKey(insertAt));
  }, [department, topicsByDept, updateDeptTopics]);

  const handleRemoveTopic = useCallback(
    (topicIndex: number) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.filter((_, index) => index !== topicIndex);
      });
    },
    [department, updateDeptTopics]
  );

  const handleFocusApplied = useCallback(() => {
    setFocusKey(null);
  }, []);

  return (
    <TabLayout
      stickyHeader={
        <div className="mt-6">
          <SegmentedButton
            ariaLabel="Department"
            options={visibleDepartments.map((dept) => ({
              label: dept.label,
              value: dept.value
            }))}
            value={department}
            onChange={handleDeptChange}
            fullWidth
          />
        </div>
      }
    >
      <div className="pt-12" />
      {error ? (
        <ConfigurationLoadError onRetry={() => refetch()} />
      ) : loading || !topicsByDept ? (
        <div className="flex items-center gap-3" data-testid="interaction-topics-loading">
          <Progress size={20} />
          <span className="text-body-medium text-sys-brand-on-surface-variant">Loading topics...</span>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <style>
            {`@keyframes topics-save-pop {
  0% { opacity: 0; transform: scale(0.85) translateY(4px); }
  18% { opacity: 1; transform: scale(1.04) translateY(0); }
  30% { opacity: 1; transform: scale(1); }
  78% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.97); }
}
.topics-save-flash { animation: topics-save-pop 1.8s ease forwards; }`}
          </style>
          <TopicsEditor
            topics={currentTopics}
            errors={errors}
            focusKey={focusKey}
            flash={fieldFlash?.dept === department ? fieldFlash : null}
            onFocusApplied={handleFocusApplied}
            onChangeTopicName={handleChangeTopicName}
            onChangeSubtopics={handleChangeSubtopics}
            onAddTopic={handleAddTopic}
            onRemoveTopic={handleRemoveTopic}
          />
          {fieldFlash?.dept === department && fieldFlash.deleted ? (
            <div
              key={fieldFlash.key}
              role="status"
              data-testid="topics-delete-flash"
              className="topics-save-flash fixed bottom-6 left-0 right-0 z-[60] mx-auto w-fit rounded-full border border-sys-brand-outline-variant bg-sys-brand-surface px-3 py-1.5 text-body-small font-medium text-sys-brand-primary shadow-sm"
            >
              ✓ Changes saved
            </div>
          ) : null}
        </div>
      )}
    </TabLayout>
  );
}
